import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Calendar, Clock, Bell, Check, Trash2, Gavel, FileText, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = { hearing: Gavel, deadline: FileText, meeting: Users, general: Bell };
const typeColors = {
  hearing: 'bg-purple-100 text-purple-700',
  deadline: 'bg-red-100 text-red-700',
  meeting: 'bg-blue-100 text-blue-700',
  general: 'bg-slate-100 text-slate-600',
};

export default function RemindersPage() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [reminders, setReminders] = useState([]);
  const [cases, setCases] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', reminder_date: '', reminder_time: '09:00', case_id: '', reminder_type: 'general' });

  const fetchData = useCallback(async () => {
    try {
      const [remRes, casesRes] = await Promise.all([
        axios.get(`${API}/reminders`, { withCredentials: true }),
        axios.get(`${API}/cases`, { withCredentials: true }),
      ]);
      setReminders(remRes.data);
      setCases(casesRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/reminders`, form, { withCredentials: true });
      setShowCreate(false);
      setForm({ title: '', description: '', reminder_date: '', reminder_time: '09:00', case_id: '', reminder_type: 'general' });
      fetchData();
    } catch {}
  };

  const toggleComplete = async (rem) => {
    try {
      await axios.put(`${API}/reminders/${rem.reminder_id}`, { is_completed: !rem.is_completed }, { withCredentials: true });
      fetchData();
    } catch {}
  };

  const deleteReminder = async (id) => {
    try {
      await axios.delete(`${API}/reminders/${id}`, { withCredentials: true });
      fetchData();
    } catch {}
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = reminders.filter(r => !r.is_completed && r.reminder_date >= today);
  const past = reminders.filter(r => r.is_completed || r.reminder_date < today);

  return (
    <DashboardLayout>
      <div data-testid="reminders-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading">
            {lang === 'ar' ? 'التذكيرات والمواعيد' : 'Reminders & Hearings'}
          </h1>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="create-reminder-btn">
                <Plus className="w-4 h-4 me-2" /> {lang === 'ar' ? 'تذكير جديد' : 'New Reminder'}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">{lang === 'ar' ? 'إنشاء تذكير' : 'Create Reminder'}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>{lang === 'ar' ? 'العنوان' : 'Title'}</Label>
                  <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="reminder-title-input" />
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'النوع' : 'Type'}</Label>
                  <Select value={form.reminder_type} onValueChange={v => setForm({ ...form, reminder_type: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hearing">{lang === 'ar' ? 'جلسة محكمة' : 'Court Hearing'}</SelectItem>
                      <SelectItem value="deadline">{lang === 'ar' ? 'موعد نهائي' : 'Deadline'}</SelectItem>
                      <SelectItem value="meeting">{lang === 'ar' ? 'اجتماع' : 'Meeting'}</SelectItem>
                      <SelectItem value="general">{lang === 'ar' ? 'عام' : 'General'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{lang === 'ar' ? 'التاريخ' : 'Date'}</Label>
                    <Input type="date" value={form.reminder_date} onChange={e => setForm({ ...form, reminder_date: e.target.value })} className="mt-1" data-testid="reminder-date-input" />
                  </div>
                  <div>
                    <Label>{lang === 'ar' ? 'الوقت' : 'Time'}</Label>
                    <Input type="time" value={form.reminder_time} onChange={e => setForm({ ...form, reminder_time: e.target.value })} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'القضية (اختياري)' : 'Case (optional)'}</Label>
                  <Select value={form.case_id} onValueChange={v => setForm({ ...form, case_id: v })}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder={lang === 'ar' ? 'بدون قضية' : 'No case'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{lang === 'ar' ? 'بدون' : 'None'}</SelectItem>
                      {cases.map(c => <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{lang === 'ar' ? 'الوصف' : 'Description'}</Label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm min-h-[60px]" />
                </div>
                <Button onClick={handleCreate} className="w-full bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="submit-reminder">
                  {lang === 'ar' ? 'إنشاء التذكير' : 'Create Reminder'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Upcoming */}
        <h2 className="text-base font-medium text-[#0F172A] font-heading mb-3">
          {lang === 'ar' ? 'القادمة' : 'Upcoming'} ({upcoming.length})
        </h2>
        <div className="space-y-2 mb-8">
          {upcoming.map(rem => <ReminderCard key={rem.reminder_id} rem={rem} onToggle={toggleComplete} onDelete={deleteReminder} lang={lang} cases={cases} />)}
          {upcoming.length === 0 && (
            <Card className="border-slate-200"><CardContent className="p-8 text-center text-sm text-[#94A3B8]">
              <Calendar className="w-10 h-10 mx-auto mb-2 text-[#94A3B8]" />
              {lang === 'ar' ? 'لا توجد تذكيرات قادمة' : 'No upcoming reminders'}
            </CardContent></Card>
          )}
        </div>

        {past.length > 0 && (
          <>
            <h2 className="text-base font-medium text-[#475569] mb-3">{lang === 'ar' ? 'سابقة / مكتملة' : 'Past / Completed'} ({past.length})</h2>
            <div className="space-y-2 opacity-60">
              {past.map(rem => <ReminderCard key={rem.reminder_id} rem={rem} onToggle={toggleComplete} onDelete={deleteReminder} lang={lang} cases={cases} />)}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function ReminderCard({ rem, onToggle, onDelete, lang, cases }) {
  const Icon = typeIcons[rem.reminder_type] || Bell;
  const colorClass = typeColors[rem.reminder_type] || typeColors.general;
  const caseName = cases.find(c => c.case_id === rem.case_id)?.title;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`reminder-${rem.reminder_id}`}>
      <CardContent className="p-4 flex items-start gap-3">
        <button onClick={() => onToggle(rem)} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${rem.is_completed ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 hover:border-[#B48E4B]'}`} data-testid={`toggle-reminder-${rem.reminder_id}`}>
          {rem.is_completed && <Check className="w-3 h-3" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${rem.is_completed ? 'line-through text-[#94A3B8]' : 'text-[#0F172A]'} text-sm font-medium`}>{rem.title}</span>
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colorClass}`}>{rem.reminder_type}</span>
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-[#94A3B8]">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {rem.reminder_date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {rem.reminder_time}</span>
          </div>
          {caseName && <p className="text-xs text-[#B48E4B] mt-1">{caseName}</p>}
          {rem.description && <p className="text-xs text-[#475569] mt-1">{rem.description}</p>}
        </div>
        <button onClick={() => onDelete(rem.reminder_id)} className="p-1 rounded hover:bg-red-50 text-[#94A3B8] hover:text-red-500" data-testid={`delete-reminder-${rem.reminder_id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </CardContent>
    </Card>
  );
}
